import { createSlice } from "@reduxjs/toolkit";
import { UserServiceImpl } from "../services/UserServiceImpl";
import { InitialUser } from "../domain/models/User";
import { ApiResponseStatus } from "../domain/enums/common/ApiResponseStatusEnum";
import { AppState } from "../store";
import { apiFilesURL, clientURL } from "../Config";
import { userProfileFields } from "../validation-schemas/user-schemas/UserProfileSchema";
import { InitialPhone } from "../domain/models/Phone";
import { getMetaParameterValue } from "../validation-schemas/SchemaFactory";
import { UserDTO } from "../domain/models/dto/UserDTO";
import { TFunction } from "i18next";
import { handleCollectionThunk } from "../services/ApiServiceImpl";
import { CookieServiceImpl } from "../services/CookieServiceImpl";
import { LocationServiceImpl } from "../services/LocationServiceImpl";
import { CoordsDTO, UrlDistanceParams } from "../domain/models/dto/AddressDTO";
import { DistanceUnit } from "../domain/enums/common/DistanceUnitEnum";

export const userSlice = createSlice({
    name: "user",
    initialState: InitialUser,
    reducers: {
        setUserProfileImage: (state, actions) => {
            state.currentUser.profileImage = actions.payload;
        },
        initPhones: (state, _actions) => {
            if (
                state.currentUser.phones?.length > 0 &&
                state.currentUser.phones?.length <
                    (getMetaParameterValue(
                        userProfileFields.phones,
                        "max"
                    ) as number)
            ) {
                state.currentUser.phones.push(InitialPhone);
            }
        },
        setUserEmail: (state, actions) => {
            state.currentUser.email = actions.payload;
        },
        setUserLocation: (state, _actions) => {
            CookieServiceImpl.setUserLocation(state.currentUser);
        },
    },
    extraReducers: (builder) => {
        handleCollectionThunk(builder, UserServiceImpl.getBlockedUsersThunk);

        // handle get /user
        builder.addCase(
            UserServiceImpl.getUserThunk.fulfilled,
            (state, action) => {
                state.currentUser = action.payload;
                userSlice.caseReducers.initPhones(state, action);
            }
        );

        // handle get /user/:id
        builder.addCase(
            UserServiceImpl.getUserByIdThunk.fulfilled,
            (state, action) => {
                const user = state.clients.find(
                    (user) => user.id === action.meta.arg
                );
                if (user) {
                    return;
                }
                state.clients.push(action.payload);
            }
        );

        // handle patch /user
        builder.addCase(
            UserServiceImpl.updateUserThunk.fulfilled,
            (state, action) => {
                state.currentUser = { ...state.currentUser, ...action.payload };
                userSlice.caseReducers.initPhones(state, action);
                userSlice.caseReducers.setUserLocation(state, action);
                return state;
            }
        );

        // handle post /login
        builder.addCase(
            UserServiceImpl.loginThunk.fulfilled,
            (state, action) => {
                state.currentUser = action.payload;
                return state;
            }
        );

        // handle get /logout
        builder.addCase(UserServiceImpl.logoutThunk.fulfilled, () => {
            return {
                ...InitialUser,
                common: { status: ApiResponseStatus.SUCCEEDED, errorKey: "" },
            };
        });

        // handle post /google-login
        builder.addCase(
            UserServiceImpl.googleLoginThunk.fulfilled,
            (state, action) => {
                state.currentUser = action.payload;
                return state;
            }
        );

        builder.addCase(
            UserServiceImpl.unblockUserThunk.fulfilled,
            (state, action) => {
                // Remove the unblocked user from the blocked users list
                state.clients = state.clients.filter(
                    (user) => user.id !== action.meta.arg.userId
                );
            }
        );
    },
});

export const selectUser = (id?: string) => (state: AppState) => {
    if (id && id !== state.user.currentUser.id) {
        return (
            state.user.clients.find((user) => user.id === id) ||
            InitialUser.currentUser
        );
    }
    return state.user.currentUser;
};

export const selectBlockedUsersCollection = () => (state: AppState) => ({
    rows: state.user.rows,
    totalPages: state.user.totalPages,
    totalRows: state.user.totalRows,
});

export const checkIsCurrentUser = (id: string) => (state: AppState) => {
    return state.user.currentUser.id === id;
};

export const selectUserFullName = (id?: string) => (state: AppState) => {
    const user = selectUser(id)(state);
    return `${user.firstName} ${user.lastName}`.trim();
};

export const selectUserAddress = (id?: string) => (state: AppState) => {
    const user = selectUser(id)(state);
    if (!isAddressCompleted(user)) {
        return "";
    }
    return `${user.address.country}, ${user.address.city}, ${user.address.address}`.trim();
};

export const selectUserCompletion = (id?: string) => (state: AppState) => {
    const user = selectUser(id)(state);
    return (
        user.firstName &&
        user.lastName &&
        isAddressCompleted(user) &&
        !!(user.phones?.length || user.telegram)
    );
};

export const selectUserProfileImage = (id?: string, thumbnail = false) => {
    return (state: AppState) => {
        const user = selectUser(id)(state);
        if (!user.profileImage) {
            return "/images/no-photo.png";
        }
        const path = thumbnail
            ? user.profileImage.thumbnailPath
            : user.profileImage.filePath;
        return path.startsWith("http") ? path : apiFilesURL + path;
    };
};

export const selectUserPhones = (id?: string) => (state: AppState) => {
    const user = selectUser(id)(state);
    return user.phones?.filter((item) => item.id);
};

export const selectUserReferralLink = (state: AppState) => {
    return `${clientURL}/registration/?referralToken=${
        selectUser()(state).referralToken
    }`;
};

export const isAddressCompleted = (user: UserDTO) => {
    return user.address?.country && user.address?.city && user.address?.address;
};

export const getUserFullName = (user: UserDTO, t: TFunction) => {
    return user?.id && !user.isDeleted
        ? `${user.firstName} ${user.lastName}`
        : t("userProfile.deletedAccount");
};

export const getUserFirstName = (user: UserDTO) => {
    return user?.id && !user.isDeleted ? user.firstName : "";
};

export const getUserCoords = async (
    user: UserDTO
): Promise<UrlDistanceParams | undefined> => {
    if (user.allowGeo) {
        const clientCoords = await LocationServiceImpl.getClientPosition();
        if (clientCoords) {
            return {
                unit: DistanceUnit.KILOMETERS,
                originCoords: clientCoords,
            };
        }
    }

    const profileCoords: CoordsDTO = {
        latitude: user.address?.latitude,
        longitude: user.address?.longitude,
    };
    if (LocationServiceImpl.areValidCoords(profileCoords)) {
        return {
            unit: DistanceUnit.KILOMETERS,
            originCoords: profileCoords,
        };
    }

    if (user.address?.id) {
        return {
            unit: DistanceUnit.KILOMETERS,
            originUuid: user.address.id,
        };
    }

    return undefined;
};

export const { setUserProfileImage, setUserEmail } = userSlice.actions;

export default userSlice.reducer;
